import React, { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ChevronDown, Loader2, Sparkles, RotateCw, Trash2, ExternalLink, Globe } from 'lucide-react';
import { useScreenRun, ScreenResults } from './InfraScreenResults';

export const CATEGORY_STYLE = {
  'O&M / Wartung': 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  'Tiefbau / Installation': 'text-orange-300 border-orange-500/40 bg-orange-500/10',
  'Engineering / Planung': 'text-sky-300 border-sky-500/40 bg-sky-500/10',
  'Software / Monitoring': 'text-violet-300 border-violet-500/40 bg-violet-500/10',
  'Ausrüstung / Komponenten': 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  'Betrieb / Services': 'text-teal-300 border-teal-500/40 bg-teal-500/10',
  'Logistik / Transport': 'text-rose-300 border-rose-500/40 bg-rose-500/10',
  'Sonstiges': 'text-muted-foreground border-border bg-secondary',
};
export const CategoryBadge = ({ category }) => (
  <span className={`inline-flex items-center text-[9px] font-medium border rounded-sm px-1.5 py-px ${CATEGORY_STYLE[category] || CATEGORY_STYLE.Sonstiges}`}>{category}</span>
);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const DACH = { hoch: 'text-emerald-300', mittel: 'text-amber-300', gering: 'text-muted-foreground' };

function ResearchStatus({ r }) {
  if (r?.status === 'done') return <span className="text-[9px] font-bold text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1 py-px">RECHERCHIERT</span>;
  if (r?.status === 'running') return <span className="text-[9px] text-amber-300 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> läuft</span>;
  if (r?.status === 'failed') return <span className="text-[9px] font-bold text-red-400 border border-red-500/40 rounded px-1 py-px" title={r.error}>FEHLER</span>;
  return <span className="text-[9px] text-muted-foreground/60 border border-border rounded px-1 py-px">offen</span>;
}

const EXTRA_LABELS = { buyer: 'Käufer', seller: 'Verkäufer', buying_fund: 'Kaufender Fonds', selling_fund: 'Verkaufender Fonds', subsector: 'Sub-Sektor', strategy: 'Strategie',
  size: 'Größe', city: 'Stadt', advisor: 'Berater', asset_type: 'Asset-Typ', deal_type: 'Deal-Typ', equity_usdm: 'Equity (USDm)', debt_usdm: 'Debt (USDm)',
  yield_pct: 'Rendite %', ebitda_multiple: 'EBITDA-Multiple', debt_provider: 'Fremdkapitalgeber', debt_type: 'Debt-Typ', maturity: 'Laufzeit', coupon_pct: 'Kupon %' };

function DealExtras({ deal }) {
  const entries = Object.entries(deal.extra || {}).filter(([, v]) => v);
  if (!entries.length && !deal.transaction_id) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`deal-extras-${deal.id}`}>
      {deal.transaction_id && <span className="text-[10px] font-mono text-muted-foreground/70 border border-border rounded-sm px-1.5 py-px">ID {deal.transaction_id}</span>}
      {entries.map(([k, v]) => (
        <span key={k} className="text-[10px] border border-border rounded-sm px-1.5 py-px"><span className="text-muted-foreground">{EXTRA_LABELS[k] || k}: </span><span className="text-foreground/90">{String(v).replace(/\|\|/g, ' + ')}</span></span>
      ))}
    </div>
  );
}

function DealDetail({ deal, fundName, rowProps, decorate, screenFilters, canManage }) {
  const r = deal.research || {};
  const [loaded, setLoaded] = useState(null);
  const screen = useScreenRun(loaded);
  React.useEffect(() => { api.infraDealScreen(deal.id).then(res => setLoaded(res.data)).catch(() => {}); }, [deal.id]);
  return (
    <div className="px-4 pb-4 space-y-3 text-xs" data-testid={`deal-detail-${deal.id}`}>
      {deal.description && <p className="text-[11px] text-muted-foreground italic leading-relaxed" data-testid={`deal-description-${deal.id}`}>{deal.description}</p>}
      <DealExtras deal={deal} />
      {r.status === 'done' ? (
        <>
          <p className="text-foreground/90 leading-relaxed">{r.target_description}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            {r.country && <span>Land: <span className="text-foreground/80">{r.country}</span></span>}
            {r.dach_relevance && <span>DACH-Relevanz: <span className={DACH[r.dach_relevance]}>{r.dach_relevance}</span>{r.dach_note && <span className="text-muted-foreground/70"> — {r.dach_note}</span>}</span>}
            {r.cost_usd != null && <span className="text-muted-foreground/50">Recherche ${Number(r.cost_usd).toFixed(2)}{r.web ? '' : ' · ohne Web'}</span>}
          </div>
          {r.add_ons?.length > 0 && <p className="text-[11px] text-amber-300/80">Zukäufe des Targets: {r.add_ons.join(' · ')}</p>}
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300/80 mb-1.5">Angrenzende Geschäftsmodelle — Angles für unsere Datenbank</p>
            <div className="grid md:grid-cols-2 gap-1.5">
              {(r.adjacent_angles || []).map((a, i) => (
                <div key={i} className="border border-border/50 rounded-sm px-2.5 py-1.5" data-testid={`deal-angle-${deal.id}-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap"><span className="text-foreground font-medium">{a.label}</span><CategoryBadge category={a.category} /></div>
                  {a.description && <p className="text-muted-foreground leading-snug">{a.description}</p>}
                  {a.rationale && <p className="text-sky-300/70 italic leading-snug">{a.rationale}</p>}
                  {a.keywords?.length > 0 && <p className="text-[10px] text-muted-foreground/60 font-mono truncate">{a.keywords.join(' · ')}</p>}
                </div>
              ))}
            </div>
          </div>
          {r.sources?.length > 0 && (
            <details className="text-[11px]"><summary className="cursor-pointer text-muted-foreground hover:text-foreground">Quellen ({r.sources.length})</summary>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">{r.sources.map((u, i) => <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="text-sky-400/80 hover:text-sky-300 inline-flex items-center gap-1 truncate max-w-[260px]"><ExternalLink className="w-2.5 h-2.5 shrink-0" />{u.replace(/^https?:\/\/(www\.)?/, '')}</a>)}</div>
            </details>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => screen.start(api.infraScreenDeal(deal.id, screenFilters ? screenFilters() : {}))} disabled={screen.run?.status === 'running'} data-testid={`deal-screen-btn-${deal.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium rounded-sm px-3 h-8 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">
              <Sparkles className="w-3 h-3" /> Firmen für diesen Deal finden
            </button>
            <span className="text-[10px] text-muted-foreground/60">Screent die Datenbank nach diesen Angles (Add-on zu {deal.target || 'diesem Target'} oder angrenzend) — nur Succession Possible + OLTA TAM</span>
          </div>
          <ScreenResults run={screen.run} rows={screen.rows} label={`für ${deal.target || deal.headline}`} prefix={`dealscreen-${deal.id}`}
            onReset={screen.reset} rowProps={rowProps} decorate={decorate} fundName={`${fundName} · ${deal.target || ''}`} />
        </>
      ) : (
        <p className="text-[11px] text-muted-foreground">{canManage ? 'Noch nicht recherchiert — „Deals recherchieren“ oben startet Perplexity + Claude für alle offenen Deals.' : 'Noch nicht recherchiert — Screening ist erst möglich, wenn der Admin die Deal-Recherche gestartet hat.'}</p>
      )}
    </div>
  );
}

export function InfraDealsTable({ deals, fundName, onChanged, rowProps, decorate, screenFilters, canManage = true }) {
  const [openId, setOpenId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const remove = async (d) => {
    setConfirmDel(null);
    try { await api.infraDeleteDeal(d.id); toast.success('Deal gelöscht'); onChanged(); } catch { toast.error('Löschen fehlgeschlagen'); }
  };
  const rerun = async (d) => {
    try { await api.infraResearchDeal(d.id); toast.success('Recherche für diesen Deal gestartet'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
  };
  if (!deals.length) return <p className="px-4 py-8 text-xs text-muted-foreground text-center" data-testid="deals-empty">{canManage ? 'Noch keine Deals — Screenshots der Deal-Tabelle oder eine Excel einspielen.' : 'Noch keine Deals für diesen Fonds — Deals einspielen kann nur der Admin.'}</p>;
  return (
    <div className="overflow-x-auto" data-testid="infra-deals-table">
      <table className="w-full text-[11px]">
        <thead className="bg-secondary/40 text-muted-foreground text-left">
          <tr>
            <th className="px-4 py-2 font-medium">Deal</th><th className="px-2 py-2 font-medium">Fund</th><th className="px-2 py-2 font-medium">Wert</th>
            <th className="px-2 py-2 font-medium">Status</th><th className="px-2 py-2 font-medium">Datum</th><th className="px-2 py-2 font-medium">Sektor</th>
            <th className="px-2 py-2 font-medium">Land / Region</th><th className="px-2 py-2 font-medium">Recherche</th><th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {deals.map(d => (
            <React.Fragment key={d.id}>
              <tr onClick={() => setOpenId(openId === d.id ? null : d.id)} data-testid={`deal-row-${d.id}`}
                className={`border-t border-border/40 cursor-pointer hover:bg-secondary/30 ${openId === d.id ? 'bg-secondary/30' : ''}`}>
                <td className="px-4 py-2">
                  <div className="flex items-start gap-1.5">
                    <ChevronDown className={`w-3 h-3 mt-0.5 text-muted-foreground/50 shrink-0 transition-transform ${openId === d.id ? 'rotate-180' : ''}`} />
                    <div>
                      <p className="text-foreground">{d.headline || d.target}</p>
                      {d.target && d.headline && <p className="text-amber-300/80">{d.target}</p>}
                      {d.research?.status === 'done' && d.research.adjacent_angles?.length > 0 && (
                        <p className="text-[10px] text-muted-foreground/70 truncate max-w-[420px]">{d.research.adjacent_angles.map(a => a.label).join(' · ')}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 text-muted-foreground">{d.fund_vehicle || '—'}</td>
                <td className="px-2 py-2 font-mono">{d.value || '—'}</td>
                <td className={`px-2 py-2 ${d.side === 'disposal' ? 'text-amber-300' : d.side === 'acquisition' ? 'text-emerald-300' : ''}`}>{d.status || '—'}</td>
                <td className="px-2 py-2 font-mono whitespace-nowrap">{fmtDate(d.date)}</td>
                <td className="px-2 py-2">{d.sector || '—'}</td>
                <td className="px-2 py-2">{[d.country, d.region].filter(Boolean).join(' · ') || '—'}</td>
                <td className="px-2 py-2"><ResearchStatus r={d.research} /></td>
                <td className="px-2 py-2" onClick={e => e.stopPropagation()}>
                  {canManage && (
                  <div className="flex items-center gap-1 justify-end">
                    {d.source === 'screenshot' && <Globe className="w-3 h-3 text-muted-foreground/30" title={`aus Screenshot ${d.source_file || ''}`} />}
                    <button onClick={() => rerun(d)} title="Recherche (neu) starten" disabled={d.research?.status === 'running'} data-testid={`deal-rerun-${d.id}`}
                      className="w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-sky-300 disabled:opacity-40"><RotateCw className="w-3 h-3" /></button>
                    {confirmDel === d.id ? (
                      <span className="inline-flex items-center gap-1 text-[10px]" data-testid={`deal-delete-confirm-${d.id}`}>
                        <button onClick={() => remove(d)} className="px-1.5 h-5 rounded-sm bg-red-600 hover:bg-red-500 text-white" data-testid={`deal-delete-yes-${d.id}`}>Ja</button>
                        <button onClick={() => setConfirmDel(null)} className="px-1.5 h-5 rounded-sm border border-border text-foreground">Nein</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDel(d.id)} title="Löschen" data-testid={`deal-delete-${d.id}`}
                        className="w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </div>
                  )}
                </td>
              </tr>
              {openId === d.id && (
                <tr className="border-t border-border/30 bg-secondary/20"><td colSpan={9}><DealDetail deal={d} fundName={fundName} rowProps={rowProps} decorate={decorate} screenFilters={screenFilters} canManage={canManage} /></td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
